import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Tables } from '@auctisync/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Invoice = Tables<'invoices'>;
type Line = Tables<'invoice_line_items'>;
type Payment = Tables<'payments'>;
type CcPreauth = Tables<'cc_preauths'>;
type InvoiceAction = Tables<'invoice_actions'>;
type Auction = Pick<Tables<'auctions'>, 'id' | 'title' | 'slug'>;
type InvoiceWithAuction = Invoice & { auctions: Auction | null };

/**
 * /admin/invoices/:id — tenant admin invoice detail. Module #9 Phase 2.
 *
 * Sections:
 *   - Header (number, status, total, paid_at, voided_at, bidder link)
 *   - Line items
 *   - Payment history (payments + cc_preauths interleaved by timestamp)
 *   - Action panel (retry, send pay-now link, charge new card, mark
 *     paid offline, void) + reconcile
 *   - Audit log (from invoice_actions)
 *
 * v1 phasing note: charge-new-card admin-side Stripe Elements entry
 * is deferred to chunk 5b polish. v1 routes "Charge a different card"
 * to send-pay-now-email — bidder self-service entry via existing
 * Module #9 /my/invoices/:id Pay Now UI. Bidder-driven path is
 * sufficient for first paying tenant; admin-driven Stripe Elements
 * is a usability polish, not a launch blocker.
 */
export function AdminInvoiceDetail() {
  const { id } = useParams();
  const [invoice, setInvoice] = useState<InvoiceWithAuction | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [preauths, setPreauths] = useState<CcPreauth[]>([]);
  const [actions, setActions] = useState<InvoiceAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{
    kind: 'success' | 'error';
    message: string;
  } | null>(null);

  const [offlineModalOpen, setOfflineModalOpen] = useState(false);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [payNowModalOpen, setPayNowModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const [invRes, lineRes, payRes, actionRes] = await Promise.all([
      supabase
        .from('invoices')
        .select('*, auctions!invoices_auction_id_fkey(id, title, slug)')
        .eq('id', id)
        .maybeSingle(),
      supabase.from('invoice_line_items').select('*').eq('invoice_id', id).order('sort_order'),
      supabase
        .from('payments')
        .select('*')
        .eq('invoice_id', id)
        .order('created_at', { ascending: false }),
      supabase
        .from('invoice_actions')
        .select('*')
        .eq('invoice_id', id)
        .order('created_at', { ascending: false }),
    ]);

    if (invRes.error) {
      setError(invRes.error.message);
      setLoading(false);
      return;
    }
    if (!invRes.data) {
      setError('Invoice not found.');
      setLoading(false);
      return;
    }

    const inv = invRes.data as InvoiceWithAuction;
    setInvoice(inv);
    setLines((lineRes.data ?? []) as Line[]);
    setPayments((payRes.data ?? []) as Payment[]);
    setActions((actionRes.data ?? []) as InvoiceAction[]);
    setError(null);

    // Load cc_preauths scoped to this user + auction (if both available).
    if (inv.user_id && inv.auction_id) {
      const { data: paRows } = await supabase
        .from('cc_preauths')
        .select('*')
        .eq('user_id', inv.user_id)
        .eq('auction_id', inv.auction_id)
        .order('created_at', { ascending: false });
      setPreauths((paRows ?? []) as CcPreauth[]);
    } else {
      setPreauths([]);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  // ─── Action handlers ─────────────────────────────────────────────────

  async function callEf<T extends Record<string, unknown> = Record<string, unknown>>(
    fnName: string,
    body: Record<string, unknown>,
  ): Promise<{ ok: boolean; data?: T; error?: string }> {
    setActionResult(null);
    const { data, error: fnErr } = (await supabase.functions.invoke(fnName, { body })) as {
      data: T | null;
      error: { message?: string } | null;
    };
    if (fnErr) return { ok: false, error: fnErr.message ?? 'unknown error' };
    if (data && typeof data === 'object' && 'error' in data) {
      const errText = (data as { error?: string; detail?: string }).error;
      const detail = (data as { error?: string; detail?: string }).detail;
      return { ok: false, error: detail ? `${errText}: ${detail}` : (errText ?? 'unknown') };
    }
    return data === null ? { ok: true } : { ok: true, data };
  }

  async function handleRetry() {
    if (!invoice) return;
    setActionBusy('retry');
    const res = await callEf('retry-invoice-payment', { invoice_id: invoice.id });
    setActionBusy(null);
    if (res.ok) {
      setActionResult({ kind: 'success', message: 'Retry initiated. Refreshing…' });
      await load();
    } else {
      setActionResult({ kind: 'error', message: `Retry failed: ${res.error}` });
    }
  }

  async function handleReconcile() {
    if (!invoice) return;
    setActionBusy('reconcile');
    const res = await callEf<{ results: { changes_applied?: string[]; status?: string }[] }>(
      'reconcile-invoice-status',
      { invoice_ids: [invoice.id] },
    );
    setActionBusy(null);
    if (res.ok) {
      const changes = res.data?.results?.[0]?.changes_applied?.length ?? 0;
      setActionResult({
        kind: 'success',
        message:
          changes > 0
            ? `Reconciled — ${changes} change${changes === 1 ? '' : 's'} applied.`
            : 'No changes — mirror already matched Stripe.',
      });
      await load();
    } else {
      setActionResult({ kind: 'error', message: `Reconcile failed: ${res.error}` });
    }
  }

  async function handleSendPayNow(customMessage?: string) {
    if (!invoice) return;
    setActionBusy('send_pay_now');
    const body: Record<string, unknown> = { invoice_id: invoice.id };
    if (customMessage) body.custom_message = customMessage;
    const res = await callEf('send-pay-now-email', body);
    setActionBusy(null);
    setPayNowModalOpen(false);
    if (res.ok) {
      setActionResult({ kind: 'success', message: 'Pay-now email queued.' });
      await load();
    } else {
      setActionResult({ kind: 'error', message: `Send failed: ${res.error}` });
    }
  }

  async function handleMarkOffline(params: {
    payment_method: string;
    reference_number: string;
    notes: string;
  }) {
    if (!invoice) return;
    setActionBusy('mark_offline');
    const res = await callEf('mark-invoice-offline', {
      invoice_id: invoice.id,
      payment_method: params.payment_method,
      reference_number: params.reference_number || undefined,
      notes: params.notes || undefined,
    });
    setActionBusy(null);
    setOfflineModalOpen(false);
    if (res.ok) {
      setActionResult({ kind: 'success', message: 'Marked paid offline.' });
      await load();
    } else {
      setActionResult({ kind: 'error', message: `Mark failed: ${res.error}` });
    }
  }

  async function handleVoid(reason: string) {
    if (!invoice || !reason.trim()) return;
    setActionBusy('void');
    const res = await callEf('void-invoice', { invoice_id: invoice.id, reason });
    setActionBusy(null);
    setVoidModalOpen(false);
    if (res.ok) {
      setActionResult({ kind: 'success', message: 'Invoice voided.' });
      await load();
    } else {
      setActionResult({ kind: 'error', message: `Void failed: ${res.error}` });
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (loading) return <div style={{ color: 'var(--text-muted)' }}>Loading invoice…</div>;
  if (error)
    return (
      <div role="alert" style={errorStyle}>
        {error}
      </div>
    );
  if (!invoice) return <div>Not found.</div>;

  const isTerminal = invoice.status === 'paid' || invoice.status === 'void';

  return (
    <div>
      <Link to="/admin/invoices" style={{ color: 'var(--brand-primary)', fontSize: '0.875rem' }}>
        ← All invoices
      </Link>
      <header style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.875rem',
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            {invoice.invoice_number ?? invoice.id.slice(0, 8)}
          </h1>
          <Badge variant={statusBadgeVariant(invoice.status)}>{invoice.status}</Badge>
          <Link
            to={`/admin/invoices/${invoice.id}/print`}
            target="_blank"
            rel="noopener"
            style={{
              marginLeft: 'auto',
              color: 'var(--brand-primary)',
              fontSize: '0.85rem',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Print / Save as PDF
          </Link>
        </div>
        <div style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          {invoice.auctions?.title ?? '—'} · Total{' '}
          <strong>{formatCents(invoice.total_cents, invoice.currency)}</strong>
          {invoice.paid_at && ` · Paid ${new Date(invoice.paid_at).toLocaleString()}`}
          {invoice.voided_at && ` · Voided ${new Date(invoice.voided_at).toLocaleString()}`}
        </div>
      </header>

      {actionResult && (
        <div
          role="status"
          style={actionResult.kind === 'success' ? successStyle : errorStyle}
          aria-live="polite"
        >
          {actionResult.message}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 18rem', gap: '1.5rem' }}>
        <div>
          <Section title="Line items">
            {lines.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>No line items.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Description</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((line) => (
                      <tr key={line.id} style={trStyle}>
                        <td style={tdStyle}>
                          {line.description}
                          <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                            (lot #{line.lot_number})
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>1</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatCents(line.line_total_cents, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>

          <Section title="Payment history">
            {payments.length === 0 && preauths.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>No payment activity yet.</p>
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gap: '0.5rem',
                }}
              >
                {[
                  ...payments.map((p) => ({ kind: 'payment' as const, row: p })),
                  ...preauths.map((p) => ({ kind: 'preauth' as const, row: p })),
                ]
                  .sort((a, b) => (b.row.created_at > a.row.created_at ? 1 : -1))
                  .map(({ kind, row }) => (
                    <li key={`${kind}-${row.id}`} style={timelineItemStyle}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <strong style={{ fontSize: '0.875rem' }}>
                          {kind === 'payment'
                            ? `Payment · ${row.status}`
                            : `Pre-auth · ${row.status}`}
                        </strong>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                          {new Date(row.created_at).toLocaleString()}
                        </span>
                      </div>
                      {kind === 'payment' && row.failure_message && (
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--error-fg, #991b1b)',
                            marginTop: '0.25rem',
                          }}
                        >
                          {row.failure_code}: {row.failure_message}
                        </div>
                      )}
                      {kind === 'preauth' && row.failure_message && (
                        <div
                          style={{
                            fontSize: '0.8rem',
                            color: 'var(--error-fg, #991b1b)',
                            marginTop: '0.25rem',
                          }}
                        >
                          {row.failure_code}: {row.failure_message}
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </Section>

          <Section title="Action history">
            {actions.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>No admin actions yet.</p>
            ) : (
              <ul
                style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.5rem' }}
              >
                {actions.map((a) => (
                  <li key={a.id} style={timelineItemStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <strong style={{ fontSize: '0.875rem' }}>
                        {humanActionType(a.action_type)} · {a.result_status}
                      </strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        {new Date(a.created_at).toLocaleString()}
                      </span>
                    </div>
                    {a.result_message && (
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--text-muted)',
                          marginTop: '0.25rem',
                        }}
                      >
                        {a.result_message}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>

        <aside>
          <Section title="Actions">
            {isTerminal ? (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>
                  Invoice is in a terminal state. Use Reconcile to refresh from Stripe.
                </p>
                <Button
                  variant="ghost"
                  onClick={() => {
                    void handleReconcile();
                  }}
                  disabled={actionBusy !== null}
                >
                  {actionBusy === 'reconcile' ? 'Reconciling…' : 'Force refresh from Stripe'}
                </Button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                <Button
                  onClick={() => {
                    void handleRetry();
                  }}
                  disabled={actionBusy !== null}
                >
                  {actionBusy === 'retry' ? 'Retrying…' : 'Retry with saved card'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setPayNowModalOpen(true)}
                  disabled={actionBusy !== null}
                >
                  Send pay-now link
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setOfflineModalOpen(true)}
                  disabled={actionBusy !== null}
                >
                  Mark paid offline
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setVoidModalOpen(true)}
                  disabled={actionBusy !== null}
                >
                  Void invoice
                </Button>
                <hr
                  style={{
                    border: 'none',
                    borderTop: '1px solid var(--admin-hairline, var(--border))',
                    margin: '0.25rem 0',
                  }}
                />
                <Button
                  variant="ghost"
                  onClick={() => {
                    void handleReconcile();
                  }}
                  disabled={actionBusy !== null}
                >
                  {actionBusy === 'reconcile' ? 'Reconciling…' : 'Force refresh from Stripe'}
                </Button>
                <p
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '0.75rem',
                    margin: '0.5rem 0 0',
                  }}
                >
                  Charge a different card via Send pay-now link → bidder enters new card. Admin-side
                  Stripe Elements entry coming in v1.1.{' '}
                  <LearnMoreLink helpSlug="charge-new-card-flow" newTab>
                    More
                  </LearnMoreLink>
                </p>
              </div>
            )}
          </Section>
        </aside>
      </div>

      {payNowModalOpen && (
        <SendPayNowModal
          onClose={() => setPayNowModalOpen(false)}
          onSubmit={(msg) => {
            void handleSendPayNow(msg);
          }}
          busy={actionBusy === 'send_pay_now'}
        />
      )}
      {offlineModalOpen && (
        <MarkOfflineModal
          onClose={() => setOfflineModalOpen(false)}
          onSubmit={(params) => {
            void handleMarkOffline(params);
          }}
          busy={actionBusy === 'mark_offline'}
        />
      )}
      {voidModalOpen && (
        <VoidModal
          onClose={() => setVoidModalOpen(false)}
          onSubmit={(reason) => {
            void handleVoid(reason);
          }}
          busy={actionBusy === 'void'}
        />
      )}
    </div>
  );
}

// ─── Components ─────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={sectionStyle}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {children}
    </section>
  );
}

function SendPayNowModal({
  onClose,
  onSubmit,
  busy,
}: {
  onClose: () => void;
  onSubmit: (customMessage?: string) => void;
  busy: boolean;
}) {
  const [msg, setMsg] = useState('');
  return (
    <ModalShell title="Send pay-now link" onClose={onClose}>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        Bidder receives an email with a link to pay this invoice. Optional custom message appears in
        the email body.
      </p>
      <textarea
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        placeholder="Optional custom message…"
        rows={3}
        maxLength={2000}
        style={textareaStyle}
      />
      <div style={modalActionsStyle}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSubmit(msg || undefined);
          }}
          disabled={busy}
        >
          {busy ? 'Sending…' : 'Send email'}
        </Button>
      </div>
    </ModalShell>
  );
}

function MarkOfflineModal({
  onClose,
  onSubmit,
  busy,
}: {
  onClose: () => void;
  onSubmit: (params: { payment_method: string; reference_number: string; notes: string }) => void;
  busy: boolean;
}) {
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <ModalShell title="Mark paid offline" onClose={onClose}>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
        Use this when payment was received outside our system (cash at pickup, mailed check, ACH,
        wire). Sets invoice to <strong>paid</strong>; recorded in the action log.
      </p>
      <label style={labelStyle}>
        Method
        <select
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          style={inputStyle}
        >
          <option value="cash">Cash</option>
          <option value="check">Check</option>
          <option value="ach">ACH</option>
          <option value="wire">Wire</option>
          <option value="other">Other</option>
        </select>
      </label>
      <label style={labelStyle}>
        Reference number (optional)
        <input
          type="text"
          value={referenceNumber}
          onChange={(e) => setReferenceNumber(e.target.value)}
          placeholder="Check #, wire confirmation, etc."
          style={inputStyle}
        />
      </label>
      <label style={labelStyle}>
        Notes (optional)
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          style={textareaStyle}
        />
      </label>
      <div style={modalActionsStyle}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSubmit({
              payment_method: paymentMethod,
              reference_number: referenceNumber,
              notes,
            });
          }}
          disabled={busy}
        >
          {busy ? 'Marking…' : 'Mark paid'}
        </Button>
      </div>
    </ModalShell>
  );
}

function VoidModal({
  onClose,
  onSubmit,
  busy,
}: {
  onClose: () => void;
  onSubmit: (reason: string) => void;
  busy: boolean;
}) {
  const [reason, setReason] = useState('');
  return (
    <ModalShell title="Void invoice" onClose={onClose}>
      <p style={{ fontSize: '0.875rem', color: 'var(--error-fg, #991b1b)' }}>
        Voiding an invoice prevents further payment attempts. Use only for unpaid invoices that
        should not be collected.
      </p>
      <label style={labelStyle}>
        Reason (required, visible in audit log)
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          required
          style={textareaStyle}
        />
      </label>
      <div style={modalActionsStyle}>
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            onSubmit(reason);
          }}
          disabled={busy || reason.trim().length === 0}
        >
          {busy ? 'Voiding…' : 'Void invoice'}
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={modalBackdropStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={modalCardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            ×
          </button>
        </div>
        <div style={{ marginTop: '1rem', display: 'grid', gap: '0.75rem' }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function statusBadgeVariant(status: string): 'default' | 'secondary' | 'outline' | 'error' {
  switch (status) {
    case 'paid':
      return 'default';
    case 'void':
    case 'refunded':
      return 'error';
    case 'partially_paid':
      return 'secondary';
    default:
      return 'outline';
  }
}

function humanActionType(t: string): string {
  switch (t) {
    case 'retry_payment':
      return 'Retry payment';
    case 'send_pay_now_link':
      return 'Sent pay-now link';
    case 'charge_new_card':
      return 'Charged new card';
    case 'mark_paid_offline':
      return 'Marked paid offline';
    case 'void':
      return 'Voided';
    case 'reconcile':
      return 'Reconciled';
    case 'bulk_run':
      return 'Bulk run';
    default:
      return t;
  }
}

// ─── Styles ────────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  background: 'var(--surface-elev)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1rem 1.25rem',
  marginBottom: '1rem',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  margin: '0 0 0.75rem 0',
  color: 'var(--text)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const trStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--admin-hairline, var(--border))',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  color: 'var(--text)',
};

const timelineItemStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  background: 'var(--surface)',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--error-bg, #fee2e2)',
  color: 'var(--error-fg, #991b1b)',
  marginBottom: '1rem',
};

const successStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--success-bg, #dcfce7)',
  color: 'var(--success-fg, #14532d)',
  marginBottom: '1rem',
};

const labelStyle: React.CSSProperties = {
  display: 'grid',
  gap: '0.25rem',
  fontSize: '0.875rem',
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  border: '1px solid var(--admin-hairline, var(--border))',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  resize: 'vertical',
  fontFamily: 'inherit',
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.5rem',
  marginTop: '0.5rem',
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 50,
  padding: '1rem',
};

const modalCardStyle: React.CSSProperties = {
  background: 'var(--surface-elev)',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1.5rem',
  width: '100%',
  maxWidth: '32rem',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
};
